import * as Yup from "yup";
import { OMN_COUNTRY_CODE } from "../constants/defaults";
import { omanVatRule } from "./omanVatValidation";

const countrySubentitySchema = (required) =>
  Yup.mixed().when("country_code", ([country_code]) => {
    if (country_code?.code === OMN_COUNTRY_CODE) {
      const shape = Yup.object().shape({
        code: required
          ? Yup.string().required("Country subentity is required")
          : Yup.string(),
        name: required ? Yup.string().required() : Yup.string(),
      });

      return required
        ? shape.required("Country subentity is required")
        : shape.nullable();
    }

    return required
      ? Yup.string().required("Country subentity is required")
      : Yup.string().nullable();
  });

export const CustomerValidation = (isIndividual, messages = {}) => {
  return Yup.object().shape({
    name: Yup.string().required("Name is required"),
    party_name: isIndividual
      ? Yup.string().nullable()
      : Yup.string().required("Business name is required"),
    party_name_in_local_language: Yup.string().nullable(),
    email: isIndividual
      ? Yup.string()
          .nullable()
          .transform((value) => (value === "" ? null : value))
          .email("Email is not a valid email")
          .max(320, "Maximum 320 characters")
      : Yup.string()
          .required("Email is required")
          .email("Email is not a valid email")
          .max(320, "Maximum 320 characters"),
    postal_address: Yup.object().shape({
      street_name: isIndividual
        ? Yup.string().nullable()
        : Yup.string().required("Street name is required"),
      additional_street_name: isIndividual
        ? Yup.string().nullable()
        : Yup.string().required("Additional street name is required"),
      address_line: isIndividual
        ? Yup.string().nullable()
        : Yup.string().required("Address line is required"),
      city_name: isIndividual
        ? Yup.string().nullable()
        : Yup.string().required("City is required"),
      country_subentity: countrySubentitySchema(!isIndividual),
      country_code: Yup.object().required("Country is required"),
      postal_zone: isIndividual
        ? Yup.string().nullable()
        : Yup.string().required("Postal zone is required"),
    }),
    telephone: Yup.string().nullable(),
    scheme_agency_id: Yup.mixed().when("postal_address.country_code", {
      is: (country) => !isIndividual && country?.code === OMN_COUNTRY_CODE,
      then: () => Yup.object().required("Registration type is required"),
      otherwise: () => Yup.mixed().nullable(),
    }),
    company_id: Yup.string().when(
      ["scheme_agency_id", "postal_address.country_code"],
      {
        is: (scheme, country) => {
          if (isIndividual) {
            return false;
          }
          if (scheme) {
            return true;
          }
          return country?.code === OMN_COUNTRY_CODE;
        },
        then: (schema) => schema.required("Registration number is required"),
        otherwise: (schema) => schema.nullable(),
      },
    ),
    passport_issuing_country_code: Yup.mixed().when("scheme_agency_id", {
      is: (val) => !isIndividual && val?.code === "PASNUM",
      then: () =>
        Yup.object()
          .shape({
            code: Yup.string().required("Passport issuing country is required"),
            label: Yup.string().required(
              "Passport issuing country is required",
            ),
          })
          .required("Passport issuing country is required"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    party_tax_scheme: isIndividual
      ? Yup.object().nullable()
      : Yup.object().when("postal_address.country_code", {
          is: (country) => country?.code === OMN_COUNTRY_CODE,
          then: () =>
            Yup.object().shape({
              tax_scheme: Yup.string().required("Tax scheme is required"),
              company_id: omanVatRule(Yup.string()),
            }),
          otherwise: () => Yup.object().nullable(),
        }),
    endpoint_scheme_id: isIndividual
      ? Yup.mixed().nullable()
      : Yup.mixed().when("postal_address.country_code", {
          is: (country) => country?.code === OMN_COUNTRY_CODE,
          then: () => Yup.object().required("Endpoint scheme id is required"),
          otherwise: () => Yup.mixed().nullable(),
        }),
    endpoint_id: isIndividual
      ? Yup.string().nullable()
      : Yup.string().when("postal_address.country_code", {
          is: (country) => country?.code === OMN_COUNTRY_CODE,
          then: () => Yup.string().required("Endpoint id is required"),
          otherwise: () => Yup.string().nullable(),
        }),
  });
};
